import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject,
  input,
  output,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';
import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { Subject, takeUntil } from 'rxjs';
import { RktButtonDirective } from '@rocketfinancialcorp/rocket-ui/button';
import {
  RktFormComponent,
  formCheckboxGroup,
  formDatepicker,
  formInput,
  formRadioGroup,
  formRow,
  formSelect,
} from '@rocketfinancialcorp/rocket-ui/form';

import { LinxProfileType, PaymentFrequency, TransactionOccurrenceType } from '@shared/constants';
import { FinancialAccountList, FinancialAccountListItem, FormModel, LinxRequestFinancialAccount } from '@shared/models';
import { FinancialAccountService } from '@shared/services';
import { ErrorUtils, formatDate, systemTimeZone } from '@shared/utils';

@Component({
  selector: 'app-linx-request-financial-account',
  templateUrl: './linx-request-financial-account.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [RktButtonDirective, FormsModule, ReactiveFormsModule, RktFormComponent],
})
export class LinxRequestFinancialAccountComponent implements OnInit, AfterViewChecked, OnDestroy {
  readonly financialAccountService = inject(FinancialAccountService);
  readonly ref = inject(ChangeDetectorRef);

  profileType = input<LinxProfileType>();

  accountHolderId = input<string>('');

  continue = output<LinxRequestFinancialAccount>();

  financialAccountForm = new UntypedFormGroup({});

  formModel: FormModel<{
    financialAccountType?: 'NEW' | 'EXISTING';
    financialAccountSubType?: string;
    existingFinancialAccountId?: string;
    verifyFinancialAccount?: Record<string, boolean>;
    debitAuthorization?: Record<string, boolean>;
    amount?: string;
    occurrenceType?: TransactionOccurrenceType;
    paymentFrequency?: PaymentFrequency;
    startDate?: string;
    endDate?: string;
  }> = {};

  financialAccountFields: FormlyFieldConfig[] = [
    formRadioGroup({
      key: 'financialAccountType',
      label: '',
      props: {
        options: [
          { label: 'Use Existing Financial Account', value: 'EXISTING' },
          { label: 'Create New Financial Account', value: 'NEW' },
        ],
        required: true,
      },
      expressions: {
        hide: () => this.isNewAccount,
      },
    }),
    formRadioGroup({
      key: 'financialAccountSubType',
      label: 'Account Type',
      props: {
        options: [
          { label: 'Checking Account', value: 'CHECKING' },
          { label: 'Saving Account', value: 'SAVING' },
          { label: 'Card Account', value: 'CARD' },
        ],
        required: true,
      },
      expressions: {
        hide: (field: FormlyFieldConfig) => field.model.financialAccountType !== 'NEW' && !this.isNewAccount,
      },
    }),
    formSelect({
      key: 'existingFinancialAccountId',
      label: 'Financial Account',
      props: {
        options: [],
        placeholder: 'Select a Financial Account',
        required: true,
      },
      className: 'linx-form-field-dropdown',
      expressions: {
        hide: (field: FormlyFieldConfig) => this.isNewAccount || field.model.financialAccountType !== 'EXISTING',
      },
    }),
    formCheckboxGroup({
      key: 'verifyFinancialAccount',
      label: 'Do you want to verify this financial account?',
      props: {
        options: [{ value: 'verify', label: 'Yes, verify this account.' }],
      },
      expressions: {
        hide: (field: FormlyFieldConfig) => !field.model.existingFinancialAccountId,
      },
    }),
  ];

  debitAuthorizationForm = new UntypedFormGroup({});

  debitAuthorizationField: FormlyFieldConfig[] = [
    formCheckboxGroup({
      key: 'debitAuthorization',
      label: '',
      props: {
        options: [{ value: 'Yes', label: 'Debit Authorization' }],
      },
    }),
    formRadioGroup({
      key: 'occurrenceType',
      label: 'Occurrence Type',
      className: 'linx-half-form-field',
      props: {
        options: [
          { label: 'Single', value: 'SINGLE' },
          { label: 'Recurring', value: 'RECURRING' },
        ],
        required: true,
      },
      expressions: {
        hide: (field: FormlyFieldConfig) => !field.model.debitAuthorization?.Yes,
      },
    }),
    formRadioGroup({
      key: 'paymentFrequency',
      label: 'Frequency',
      className: 'linx-half-form-field',
      props: {
        options: [
          { label: 'Daily', value: PaymentFrequency.DAILY },
          { label: 'Weekly', value: PaymentFrequency.WEEKLY },
          { label: 'Biweekly', value: PaymentFrequency.BIWEEKLY },
          { label: 'Monthly', value: PaymentFrequency.MONTHLY },
          { label: 'Quarterly', value: PaymentFrequency.QUARTERLY },
          { label: 'Yearly', value: PaymentFrequency.YEARLY },
        ],
        required: true,
      },
      expressions: {
        hide: (field: FormlyFieldConfig) => !field.model.debitAuthorization?.Yes || field.model?.occurrenceType === 'SINGLE',
      },
    }),
    formRow([
      formDatepicker({
        key: 'startDate',
        label: 'Start Date',
        className: 'linx-half-form-field',
        props: {
          required: true,
          placeholder: 'MM/DD/YY',
          minDate: formatDate(toZonedTime(new Date(), systemTimeZone)),
          maxDate: NgbDate.from({ day: 1, month: 1, year: new Date().getFullYear() + 120 }),
        },
        expressions: {
          hide: (field: FormlyFieldConfig) => !field.model.debitAuthorization?.Yes,
        },
      }),
      formDatepicker({
        key: 'endDate',
        label: 'End Date',
        props: {
          required: true,
          placeholder: 'MM/DD/YY',
          maxDate: NgbDate.from({ day: 1, month: 1, year: new Date().getFullYear() + 120 }),
        },
        expressions: {
          'props.minDate': (field: FormlyFieldConfig) => {
            return field.model?.startDate ? formatDate(toZonedTime(new Date(field.model.startDate), systemTimeZone)) : null;
          },
          'props.disabled': (field: FormlyFieldConfig) => field.model?.occurrenceType === 'SINGLE',
          hide: (field: FormlyFieldConfig) => !field.model.debitAuthorization?.Yes,
        },
      }),
    ]),
    formInput({
      key: 'amount',
      label: 'Transaction amount',
      props: {
        pattern: /^\d+$|(?=^.+$)^\d+\.\d{0,2}$/,
        required: true,
      },
      validation: {
        messages: {
          pattern: 'Please enter a valid amount.',
        },
      },
      className: 'linx-half-form-field',
      expressions: {
        hide: (field: FormlyFieldConfig) => !field.model.debitAuthorization?.Yes,
      },
    }),
  ];

  get showDebitAuthorization(): boolean {
    if (this.formModel.existingFinancialAccountId) {
      const existingFinancialAccount = this.financialAccounts.find((item) => item.id === this.formModel.existingFinancialAccountId);
      return existingFinancialAccount?.category === 'EXTERNAL';
    }

    return this.financialAccountForm.valid;
  }

  financialAccountsLoading = false;

  financialAccounts!: FinancialAccountListItem[];

  get isNewAccount(): boolean {
    return this.profileType() === LinxProfileType.New;
  }

  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    if (!this.isNewAccount) {
      this.getFinancialAccounts();
    }
  }

  ngAfterViewChecked(): void {
    this.ref.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onClickContinue(): void {
    const {
      financialAccountType = 'NEW',
      financialAccountSubType,
      existingFinancialAccountId,
      verifyFinancialAccount,
      debitAuthorization,
      amount,
      occurrenceType,
      paymentFrequency,
      startDate,
      endDate,
    } = this.formModel;

    const formData = { financialAccountType } as LinxRequestFinancialAccount;

    if (financialAccountType === 'NEW') {
      formData.financialAccountSubType = financialAccountSubType;
    } else {
      const existingFinancialAccount = this.financialAccounts.find((item) => item.id === existingFinancialAccountId);
      formData.existingFinancialAccount = existingFinancialAccount;
      formData.financialAccountSubType = existingFinancialAccount?.subtype;
    }

    if (verifyFinancialAccount && verifyFinancialAccount.verify) {
      formData.verifyFinancialAccount = verifyFinancialAccount.verify;
    }

    if (debitAuthorization?.Yes) {
      formData.debitAuthorization = {
        amount,
        occurrenceType,
        paymentFrequency,
        timeZone: systemTimeZone,
        currency: 'USD',
      };
      if (startDate) {
        formData.debitAuthorization.startDate = format(new Date(startDate), 'yyyy-MM-dd');
      }
      if (endDate) {
        formData.debitAuthorization.endDate = format(new Date(endDate), 'yyyy-MM-dd');
      }
      formData.verifyFinancialAccount = true;
    }

    this.continue.emit(formData);
  }

  onClearBtnClick(): void {
    this.financialAccountForm.reset();
  }

  getFinancialAccounts(): void {
    if (!this.accountHolderId()) {
      return;
    }
    this.financialAccountsLoading = true;

    this.financialAccountService
      .getCustomerFinancialAccounts({ customerId: this.accountHolderId() })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.onFetchComplete(response);
        },
        error: (error) => {
          this.onFetchComplete();
          ErrorUtils.catchError('customerService.getCustomerFinancialAccounts', error);
        },
      });
  }

  onFetchComplete(response?: FinancialAccountList): void {
    const { items = [] } = response || {};

    this.financialAccounts = items;

    if (items.length) {
      const financialAccountOptions: Record<string, string | boolean>[] = [];

      items.forEach((item) => {
        const availableBalance = item.availableBalance ? item.availableBalance : '0.00';
        const account = {
          value: item.id,
          label: `${item.name ?? item.displayName} | Available Balance $ ${availableBalance}`,
        };
        financialAccountOptions.push(account);
      });
      if (this.financialAccountFields[2]?.props) {
        this.financialAccountFields[2].props.options = financialAccountOptions;
      }
    }

    this.financialAccountsLoading = false;
  }
}
